import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import MoneyUtils from '../../../utils/moneyUtils';

const ReorderDialog = ({ isOpen, order, onClose, onConfirmReorder }) => {
  const [unavailableItems, setUnavailableItems] = useState([
    // Mock unavailable items for demonstration
    {
      id: 'item_3',
      name: 'Seasonal Salad',
      reason: 'No longer available'
    }
  ]);

  const [selectedSubstitutions, setSelectedSubstitutions] = useState({});

  const handleSubstitutionChange = (itemId, substitution) => {
    setSelectedSubstitutions(prev => ({
      ...prev,
      [itemId]: substitution
    }));
  };

  const handleConfirmReorder = () => {
    const reorderData = {
      originalOrder: order,
      unavailableItems,
      substitutions: selectedSubstitutions
    };
    onConfirmReorder(reorderData);
    onClose();
  };

  if (!isOpen || !order) return null;

  const availableItems = order?.items?.filter(item => 
    !unavailableItems?.some(unavailable => unavailable?.id === item?.id)
  );

  const totalAvailablePrice = availableItems?.reduce((sum, item) => {
    const addonTotal = item?.addons?.reduce((aSum, addon) => aSum + (addon.price || 0) * (addon.quantity || 1), 0) || 0;
    return sum + ((item?.price || 0) + addonTotal) * (item?.quantity || 1);
  }, 0);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-modal" onClick={onClose} />
      {/* Dialog */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 lg:inset-x-auto lg:left-1/2 lg:-translate-x-1/2 lg:w-full lg:max-w-md bg-card rounded-lg shadow-lg z-modal max-h-[90vh] overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                <Image
                  src={order?.restaurant?.image}
                  alt={order?.restaurant?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Reorder
                </h2>
                <p className="text-sm text-muted-foreground">
                  {order?.restaurant?.name}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
              aria-label="Close dialog"
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Available Items */}
            {availableItems?.length > 0 && (
              <div>
                <h3 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="Check" size={16} className="text-success mr-2" />
                  Available Items ({availableItems?.length})
                </h3>
                <div className="space-y-2">
                  {availableItems?.map((item) => (
                    <div
                      key={item?.id}
                      className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="w-6 h-6 bg-success/10 text-success rounded text-xs flex items-center justify-center font-medium">
                          {item?.quantity}
                        </span>
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">
                            {item?.name}
                            {(item?.portion?.name || item?.portionName) && (
                              <span className="text-primary text-[11px] font-bold ml-1">
                                [{item?.portion?.name || item?.portionName}]
                              </span>
                            )}
                          </span>
                          {item?.addons?.length > 0 && (
                            <div className="flex flex-wrap gap-x-2 text-[10px] text-muted-foreground/70">
                              {item.addons.map((addon, aIdx) => (
                                <span key={aIdx}>+ {addon.name}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {(() => {
                          const addonTotal = item?.addons?.reduce((aSum, addon) => aSum + (addon.price || 0) * (addon.quantity || 1), 0) || 0;
                          return MoneyUtils.format(((item?.price || 0) + addonTotal) * (item?.quantity || 1));
                        })()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unavailable Items */}
            {unavailableItems?.length > 0 && (
              <div>
                <h3 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon
                    name="AlertTriangle"
                    size={16}
                    className="text-warning mr-2"
                  />
                  Unavailable Items ({unavailableItems?.length})
                </h3>
                <div className="space-y-3">
                  {unavailableItems?.map((unavailableItem) => {
                    const originalItem = order?.items?.find(
                      (item) => item?.id === unavailableItem?.id
                    );
                    return (
                      <div
                        key={unavailableItem?.id}
                        className="p-3 bg-warning/5 border border-warning/20 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="w-6 h-6 bg-warning/10 text-warning rounded text-xs flex items-center justify-center font-medium">
                              {originalItem?.quantity}
                            </span>
                            <span className="text-sm text-foreground">
                              {unavailableItem?.name}
                            </span>
                          </div>
                          <span className="text-xs text-warning font-medium">
                            {unavailableItem?.reason}
                          </span>
                        </div>
                        {/* Suggested Substitutions */}
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground mb-2">
                            Suggested alternatives:
                          </p>
                          <div className="space-y-1">
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="radio"
                                name={`substitution_${unavailableItem?.id}`}
                                value="skip"
                                checked={
                                  selectedSubstitutions?.[
                                    unavailableItem?.id
                                  ] === "skip" ||
                                  !selectedSubstitutions?.[unavailableItem?.id]
                                }
                                onChange={() =>
                                  handleSubstitutionChange(
                                    unavailableItem?.id,
                                    "skip"
                                  )
                                }
                                className="text-primary"
                              />
                              <span>Skip this item</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="radio"
                                name={`substitution_${unavailableItem?.id}`}
                                value="garden_salad"
                                checked={
                                  selectedSubstitutions?.[
                                    unavailableItem?.id
                                  ] === "garden_salad"
                                }
                                onChange={() =>
                                  handleSubstitutionChange(
                                    unavailableItem?.id,
                                    "garden_salad"
                                  )
                                }
                                className="text-primary"
                              />
                              <span>Garden Salad (+$2.00)</span>
                            </label>
                            <label className="flex items-center space-x-2 text-sm">
                              <input
                                type="radio"
                                name={`substitution_${unavailableItem?.id}`}
                                value="caesar_salad"
                                checked={
                                  selectedSubstitutions?.[
                                    unavailableItem?.id
                                  ] === "caesar_salad"
                                }
                                onChange={() =>
                                  handleSubstitutionChange(
                                    unavailableItem?.id,
                                    "caesar_salad"
                                  )
                                }
                                className="text-primary"
                              />
                              <span>Caesar Salad (+$3.50)</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-medium text-foreground mb-2">
                Reorder Summary
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Available items</span>
                  <span className="text-foreground">
                    {MoneyUtils.format(totalAvailablePrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Substitutions</span>
                  <span className="text-foreground">{MoneyUtils.format(0)}</span>
                </div>
                <div className="flex justify-between font-medium text-foreground pt-1 border-t border-border">
                  <span>Estimated Total</span>
                  <span>{MoneyUtils.format(totalAvailablePrice)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex space-x-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleConfirmReorder}
                iconName="ShoppingCart"
                iconPosition="left"
                className="flex-1"
              >
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReorderDialog;